import React from "react";
import { Award, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const RPG_TEMPLATES = ["rpg3d", "explorer3d", "collector3d", "platformer3d", "fps3d", "battle3d", "horror3d"];
const PERK_STATS = [["coins", "Coins"], ["speed", "Speed"], ["damage", "Damage"], ["jump", "Jump"]];

// Hand-editable Title registry. Earned titles grant a passive perk (and can gate shop items /
// dialogue via titleReq). Writes config.titles.
export function TitlesPanel({ config, setField, is3D }) {
  const tpl = config?.template || "";
  const titles = Array.isArray(config?.titles) ? config.titles : [];
  const show = is3D && (RPG_TEMPLATES.includes(tpl) || titles.length > 0);
  if (!show) return null;

  const setTitles = (arr) => setField("titles", arr);
  const update = (i, patch) => setTitles(titles.map((t, k) => (k === i ? { ...t, ...patch } : t)));
  const updatePerk = (i, patch) => setTitles(titles.map((t, k) => (k === i ? { ...t, perk: { stat: "coins", value: 0.1, ...(t.perk || {}), ...patch } } : t)));
  const add = () => setTitles([...titles, { name: "New Title", icon: "🏅", perk: { stat: "coins", value: 0.1 }, desc: "" }]);
  const remove = (i) => setTitles(titles.filter((_, k) => k !== i));

  return (
    <div className="pt-2 border-t border-[hsl(var(--border))] space-y-3" data-testid="titles-panel">
      <div className="flex items-center gap-1.5">
        <div className="text-xs font-bold flex items-center gap-1.5"><Award className="w-3.5 h-3.5 text-[hsl(var(--primary))]" /> Titles &amp; Perks</div>
        <span className="ml-auto text-[10px] text-[hsl(var(--muted-foreground))]">earned titles grant passive perks</span>
      </div>
      <p className="text-[10px] text-[hsl(var(--muted-foreground))]">Define titles players can earn (e.g. from completing the Codex). Each earned title grants its perk permanently — perks stack. Reference a title in a shop item&rsquo;s or dialogue choice&rsquo;s <b>titleReq</b> to gate it.</p>

      {titles.map((t, i) => (
        <div key={i} className="rounded-xl border border-[hsl(var(--border))] p-2.5 space-y-1.5" data-testid={`title-row-${i}`}>
          <div className="flex items-center gap-1.5">
            <Input value={t.icon || "🏅"} onChange={(e) => update(i, { icon: e.target.value.slice(0, 4) })} className="h-8 w-11 text-center px-1" data-testid={`title-icon-${i}`} />
            <Input value={t.name || ""} onChange={(e) => update(i, { name: e.target.value })} placeholder="Title name" className="h-8 flex-1 min-w-0" data-testid={`title-name-${i}`} />
            <button type="button" onClick={() => remove(i)} className="h-8 w-8 grid place-items-center rounded-lg border border-[hsl(var(--border))] hover:border-rose-500/60 text-[hsl(var(--muted-foreground))] hover:text-rose-500" data-testid={`title-remove-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
          <div className="flex items-center gap-1.5 text-[10px] text-[hsl(var(--muted-foreground))]">
            <span className="font-semibold">Perk: +</span>
            <Input type="number" min={1} max={200} value={Math.round((t.perk?.value ?? 0.1) * 100)} onChange={(e) => updatePerk(i, { value: Math.max(0.01, (+e.target.value || 10) / 100) })} className="h-7 w-16 text-center px-1" data-testid={`title-perk-value-${i}`} />
            <span>%</span>
            <Select value={t.perk?.stat || "coins"} onValueChange={(v) => updatePerk(i, { stat: v })}>
              <SelectTrigger className="h-7 w-[100px] text-xs" data-testid={`title-perk-stat-${i}`}><SelectValue /></SelectTrigger>
              <SelectContent>{PERK_STATS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <span className="ml-auto font-semibold">🎭 emote</span>
            <Input value={t.emote || ""} onChange={(e) => update(i, { emote: e.target.value.slice(0, 4) })} placeholder="🎉" className="h-7 w-11 text-center px-1" data-testid={`title-emote-${i}`} />
          </div>
          <Input value={t.desc || ""} onChange={(e) => update(i, { desc: e.target.value.slice(0, 120) })} placeholder="Short description (optional)" className="h-7 text-[11px]" data-testid={`title-desc-${i}`} />
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={add} className="w-full h-8 text-xs" data-testid="add-title-btn"><Plus className="w-3.5 h-3.5" /> Add title</Button>
    </div>
  );
}
